import { useTranslation } from 'react-i18next';
import { MapLocationData } from '@hero/web-components';

export interface MarkerCardTooltipProps {
  /** Location data */
  location: MapLocationData;
  /** Index of the location */
  index: number;
  /** Whether this is the origin/pickup location */
  isOrigin?: boolean;
  /** Whether this location is currently being edited */
  isEditing?: boolean;
  /** Optional distance information */
  distance?: {
    text: string;
    value: number;
  };
  /** Optional duration information */
  duration?: {
    text: string;
    value: number;
  };
  /** Optional custom label/title */
  title?: string;
}

/**
 * MarkerCard - Reusable tooltip component for map markers
 * Shows location information that appears above markers
 */
export function MarkerCard({
  location,
  index,
  isOrigin = false,
  isEditing = false,
  distance,
  duration,
  title,
}: MarkerCardTooltipProps) {
  const { t, i18n } = useTranslation();
  const dir = i18n.dir();

  const displayTitle =
    title ||
    (isOrigin
      ? t('location.pickup', 'Pickup Location')
      : `${t('location.dropoff', 'Dropoff')} ${index}`);
  const label = location.fullAddress || location.address?.fullAddress || '';

  return (
    <div className="p-2 text-center bg-white rounded-lg shadow-lg" dir={dir}>
      <p className="text-sm font-medium text-gray-800">{displayTitle}</p>
      <p className="mt-1 text-xs text-gray-500 line-clamp-2">{label}</p>
      {distance && duration && (
        <div className="mt-0.5 space-y-0.5">
          <p
            className={`text-xs font-medium ${
              isEditing
                ? 'text-orange-600'
                : isOrigin
                ? 'text-primary'
                : 'text-green-600'
            }`}>
            {distance.text}
          </p>
          <p className="text-xs font-medium text-gray-500">{duration.text}</p>
        </div>
      )}
    </div>
  );
}
